import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

function DeliveryTrendsChart({ trends }) {
  if (!trends || trends.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <TrendingUp className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500 font-montserrat">No hay datos de tendencias</p>
      </div>
    );
  }

  // Format dates for display
  const formattedData = trends.map(trend => ({
    ...trend,
    displayDate: format(new Date(trend.date), 'dd/MM', { locale: es })
  }));

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <div className="mb-6">
        <h3 className="text-lg font-bold text-slate-900 font-montserrat mb-2">
          Tendencia Diaria de Entregas
        </h3>
        <p className="text-sm text-slate-600 font-montserrat">
          Evolución de entregas totales, entregadas y rechazadas
        </p>
      </div>

      {/* Chart */}
      <div className="h-96">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={formattedData}
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis 
              dataKey="displayDate"
              tick={{ fill: '#64748b', fontSize: 12 }}
              tickMargin={10}
            />
            <YAxis tick={{ fill: '#64748b', fontSize: 12 }} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontFamily: 'Montserrat'
              }}
              labelFormatter={(value) => `Fecha: ${value}`}
            />
            <Legend 
              wrapperStyle={{ 
                fontFamily: 'Montserrat',
                fontSize: '14px',
                paddingTop: '20px'
              }}
            />
            <Line 
              type="monotone" 
              dataKey="total" 
              stroke="#B1336E" 
              strokeWidth={3}
              name="Total"
              dot={{ fill: '#B1336E', r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line 
              type="monotone" 
              dataKey="delivered" 
              stroke="#67A43C" 
              strokeWidth={3}
              name="Entregados"
              dot={{ fill: '#67A43C', r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line 
              type="monotone" 
              dataKey="rejected" 
              stroke="#ef4444" 
              strokeWidth={2}
              strokeDasharray="5 5"
              name="Rechazados"
              dot={{ fill: '#ef4444', r: 3 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Summary Stats */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gradient-to-r from-[#B1336E]/5 to-[#B1336E]/10 rounded-lg p-4 border-l-4 border-[#B1336E]">
          <p className="text-xs text-slate-600 font-montserrat mb-1">Promedio Diario Total</p>
          <p className="text-2xl font-bold text-[#B1336E] font-montserrat">
            {(trends.reduce((sum, t) => sum + t.total, 0) / trends.length).toFixed(0)}
          </p>
        </div>
        
        <div className="bg-gradient-to-r from-[#67A43C]/5 to-[#67A43C]/10 rounded-lg p-4 border-l-4 border-[#67A43C]">
          <p className="text-xs text-slate-600 font-montserrat mb-1">Promedio Diario Entregados</p>
          <p className="text-2xl font-bold text-[#67A43C] font-montserrat">
            {(trends.reduce((sum, t) => sum + t.delivered, 0) / trends.length).toFixed(0)}
          </p>
        </div>
        
        <div className="bg-gradient-to-r from-red-50 to-red-100 rounded-lg p-4 border-l-4 border-red-500">
          <p className="text-xs text-slate-600 font-montserrat mb-1">Promedio Diario Rechazados</p>
          <p className="text-2xl font-bold text-red-600 font-montserrat">
            {(trends.reduce((sum, t) => sum + t.rejected, 0) / trends.length).toFixed(0)}
          </p>
        </div>
      </div>
    </div>
  );
}

export default DeliveryTrendsChart;